/**
 * An application's own header, and the one place its styling is resolved.
 *
 * The builder and the viewer both render THIS component, so what an admin
 * designs is literally what ships — there is no second copy of the markup to
 * drift out of step. In the builder the header is clickable and opens the
 * properties panel, the same way clicking a layer opens Symbology.
 */

import type { HeaderTheme } from '../../types';
import HeaderLogo, { DEFAULT_LOGO_SIZE } from './HeaderLogo';

/**
 * Font stacks offered in the designer.
 *
 * Deliberately limited to families that resolve without a webfont download:
 * a header that silently falls back to Times on the client's machine is worse
 * than one that never offered the choice.
 */
export const HEADER_FONTS: { id: string; label: string; stack: string }[] = [
  { id: 'system', label: 'System', stack: '' },
  {
    id: 'sans',
    label: 'Sans',
    stack: '"Segoe UI", Arial, Helvetica, sans-serif',
  },
  {
    id: 'serif',
    label: 'Serif',
    stack: 'Georgia, "Times New Roman", Times, serif',
  },
  {
    id: 'condensed',
    label: 'Condensed',
    stack: '"Arial Narrow", "Segoe UI", Roboto, sans-serif',
  },
  {
    id: 'mono',
    label: 'Mono',
    stack: '"Cascadia Code", Consolas, "Courier New", monospace',
  },
];

export const HEADER_DEFAULTS = {
  titleSize: 15,
  subtitleSize: 12,
  titleWeight: 600 as const,
  subtitlePlacement: 'inline' as const,
  titleAlign: 'left' as const,
  logoSize: DEFAULT_LOGO_SIZE,
};

/** Is this background dark enough that light text reads better on it? */
function isDarkBackground(colour?: string): boolean {
  if (!colour || !/^#[0-9a-f]{6}$/i.test(colour)) return false;
  const r = parseInt(colour.slice(1, 3), 16);
  const g = parseInt(colour.slice(3, 5), 16);
  const b = parseInt(colour.slice(5, 7), 16);
  // Rec. 601 luma; below the midpoint the background reads as dark.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
}

export interface ResolvedHeaderStyle {
  background: string;
  title: string;
  subtitleColour: string;
  align: 'left' | 'center' | 'right';
  subtitle: string;
  placement: 'inline' | 'below';
  fontFamily?: string;
  titleSize: number;
  subtitleSize: number;
  titleWeight: number;
  titleItalic: boolean;
  showLogo: boolean;
  logoUrl?: string;
  logoSize: number;
}

/**
 * Turn a stored theme into concrete values.
 *
 * The text colours are DERIVED from the background when unset: an admin who
 * picks a dark brand colour should not also have to remember to switch the
 * text to white for it to stay readable.
 */
export function resolveHeaderStyle(theme: HeaderTheme = {}): ResolvedHeaderStyle {
  const dark = isDarkBackground(theme.background);
  const title = theme.textColor ?? (dark ? '#F8FAFC' : 'var(--glass-text)');

  return {
    background: theme.background ?? 'var(--bv-surface-raised)',
    title,
    subtitleColour:
      theme.subtitleColor ?? (theme.textColor ? theme.textColor : dark ? 'rgba(248,250,252,0.78)' : '#64748B'),
    align: theme.titleAlign ?? HEADER_DEFAULTS.titleAlign,
    subtitle: theme.subtitle?.trim() ?? '',
    placement: theme.subtitlePlacement ?? HEADER_DEFAULTS.subtitlePlacement,
    fontFamily: theme.fontFamily || undefined,
    titleSize: theme.titleSize ?? HEADER_DEFAULTS.titleSize,
    subtitleSize: theme.subtitleSize ?? HEADER_DEFAULTS.subtitleSize,
    titleWeight: theme.titleWeight ?? HEADER_DEFAULTS.titleWeight,
    titleItalic: theme.titleItalic ?? false,
    showLogo: theme.showLogo ?? true,
    logoUrl: theme.logoUrl?.trim() || undefined,
    logoSize: theme.logoSize ?? HEADER_DEFAULTS.logoSize,
  };
}

export default function AppHeader({
  title,
  theme,
  onEdit,
  editing = false,
}: {
  title: string;
  theme?: HeaderTheme;
  /** Builder only: clicking the header opens the properties panel. */
  onEdit?: () => void;
  /** Draws the "being edited" outline while the panel is open. */
  editing?: boolean;
}) {
  const s = resolveHeaderStyle(theme);

  const titleNode = (
    <span
      className="truncate"
      style={{
        color: s.title,
        fontSize: s.titleSize,
        fontWeight: s.titleWeight,
        fontStyle: s.titleItalic ? 'italic' : undefined,
      }}
    >
      {title}
    </span>
  );

  const subtitleNode = s.subtitle ? (
    <span
      className="truncate"
      style={{ color: s.subtitleColour, fontSize: s.subtitleSize }}
    >
      {s.subtitle}
    </span>
  ) : null;

  const content =
    s.placement === 'inline' ? (
      // Title and subtitle on one line, split by a hairline rule — the product
      // / application pairing every GIS suite's header uses.
      <h1
        className={`flex min-w-0 items-baseline gap-2 ${
          s.align === 'center' ? 'justify-center' : s.align === 'right' ? 'justify-end' : ''
        }`}
      >
        {titleNode}
        {subtitleNode && (
          <>
            <span
              aria-hidden="true"
              className="flex-shrink-0 self-stretch border-l"
              style={{ borderColor: s.subtitleColour, opacity: 0.4 }}
            />
            {subtitleNode}
          </>
        )}
      </h1>
    ) : (
      <div
        className={`min-w-0 ${
          s.align === 'center' ? 'text-center' : s.align === 'right' ? 'text-right' : ''
        }`}
      >
        <h1 className="flex min-w-0">{titleNode}</h1>
        {subtitleNode && <p className="flex min-w-0 leading-tight">{subtitleNode}</p>}
      </div>
    );

  return (
    <header
      className="flex flex-shrink-0 items-center gap-2.5 border-b border-slate-200 px-3 py-1.5"
      style={{ background: s.background, fontFamily: s.fontFamily }}
    >
      {s.showLogo && <HeaderLogo url={s.logoUrl} size={s.logoSize} />}

      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit header design"
          aria-pressed={editing}
          title="Click to design this header"
          className={`min-w-0 flex-1 rounded-md px-1.5 py-1 text-left transition ${
            editing ? 'ring-2 ring-blue-400' : 'hover:ring-1 hover:ring-blue-300'
          }`}
        >
          {content}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{content}</div>
      )}

      {/* Balances a centred title against the logo on the left.
          A second, invisible copy of the logo rather than a fixed-width box:
          a custom logo is whatever width its aspect ratio makes it, so the
          only spacer guaranteed to match is the logo itself. */}
      {s.align === 'center' && s.showLogo && (
        <div aria-hidden="true" className="invisible">
          <HeaderLogo url={s.logoUrl} size={s.logoSize} />
        </div>
      )}
    </header>
  );
}
